import type { HelpContent } from './entries.tr';
import type { HELP_FAZ8_TR } from './entries.faz8.tr';

type En = Pick<HelpContent, 'title' | 'short'> & Partial<Pick<HelpContent, 'whatItDoes'>>;

/** Phase 8 (finance & analytics) help — English titles and short texts (ADR-801). */
export const HELP_FAZ8_EN: Record<keyof typeof HELP_FAZ8_TR, En> = {
  'page.sales_report': {
    title: 'Sales report',
    short: 'Orders, net sales, average order, returns and cancellations, new and returning customers.',
    whatItDoes:
      'Counts confirmed orders placed in the period (paid, processing, shipped, delivered, completed); orders awaiting payment are listed separately. Amounts in TRY incl. VAT, compared with the previous period of equal length. No estimated or sample figures.',
  },
  'reports.period': {
    title: 'Period',
    short: 'Days covered (Turkey time, both ends included). The previous period has the same length.',
  },
  'reports.interval': { title: 'Group by', short: 'Day, week (starting Monday), month or year.' },
  'reports.download': { title: 'Download', short: 'Downloads the report as CSV or Excel (XLSX).' },
  'sales_report.store': { title: 'Store', short: 'Orders of the selected store only.' },
  'sales_report.channel': { title: 'Channel', short: 'Web store, dealer portal, panel order or API.' },
  'sales_report.segment': { title: 'Customer type', short: 'Retail or dealer (company) orders.' },
  'sales_report.summary': {
    title: 'Gross to net',
    short:
      'Gross product sales minus discounts plus shipping is the order value; minus returns and cancellations is net sales.',
  },
  'sales_report.new_customers': {
    title: 'New and returning customers',
    short: 'New: the order is the first confirmed order of that account or e-mail.',
  },
  'dashboard.sales': {
    title: 'Sales summary',
    short:
      'Net sales and orders today and over the last 30 days, compared with yesterday and the previous 30 days.',
  },
  'sales_report.breakdown': {
    title: 'Breakdown',
    short:
      'Net sales by product, variant, category, brand, country, channel, store, customer type or payment method.',
  },
  'page.finance_inputs': {
    title: 'Finance inputs',
    short: 'Costs used for profit: payment fee rules, shipping costs and business expenses.',
    whatItDoes:
      'Payment provider fees and carrier costs are not recorded automatically; enter them here. Missing items show as "no data" in reports; nothing is estimated unless you set estimate rules.',
  },
  'finance_inputs.fee_rule': {
    title: 'Payment fee rule',
    short: 'Rate and fixed fee per payment method (optionally per installment count), with validity dates.',
  },
  'finance_inputs.shipment_cost': {
    title: 'Shipping cost',
    short: 'Amount paid to the carrier for the shipment (incl. VAT), with VAT and invoice number.',
  },
  'finance_inputs.import': {
    title: 'Upload carrier invoice',
    short: 'Matches the carrier invoice file (CSV/Excel) to shipments by tracking number; preview first.',
  },
  'finance_inputs.shipping_rule': {
    title: 'Shipping cost estimate rule',
    short: 'Approximate cost per shipment by carrier and package weight (excl. VAT).',
  },
  'finance_inputs.expense': {
    title: 'Business expense',
    short: 'Rent, salaries, software, packaging etc.; amount incl. VAT, VAT separately.',
  },
  'page.finance_report': {
    title: 'Finance report',
    short: 'Profitability, cash flow, VAT summary, receivables; product and channel profitability.',
    whatItDoes:
      'Profit is on order basis and excl. VAT: revenue, cost of goods at sale, shipping, payment fees, ads and expenses. Cash flow is by transaction date. Each line shows whether data is complete, partly estimated or missing.',
  },
  'finance_report.status': {
    title: 'Data status',
    short: 'Complete, partial (some estimated or unknown) or no data (missing, not counted as zero).',
  },
  'finance_report.cash': {
    title: 'Cash flow',
    short: 'Payments and dealer collections in; refunds, fees, carriers, expenses, ads out.',
  },
  'finance_report.vat': {
    title: 'VAT summary',
    short: 'VAT on sales vs VAT paid on expenses and shipping. Not a tax return.',
  },
  'finance_report.products': {
    title: 'Product and channel profitability',
    short: 'Revenue, COGS and gross profit per product; contribution per channel.',
  },
  'page.stock_report': {
    title: 'Stock report',
    short:
      'Stock value, by warehouse and category, turnover, slow movers; expiry and critical stock summary.',
    whatItDoes:
      'Stock value is units on hand × current product cost (excl. VAT). Turnover is units sold in the period (net of returns) divided by average stock, computed from the stock ledger.',
  },
  'stock_report.value': {
    title: 'Stock value',
    short: 'Units on hand × current cost (excl. VAT); items without cost are not valued.',
  },
  'stock_report.turnover': {
    title: 'Turnover and days of inventory',
    short: 'Units sold ÷ average stock; days = period days ÷ turnover.',
  },
  'stock_report.slow': {
    title: 'Slow-moving',
    short: 'In stock with no sale for the chosen number of days (default 90).',
  },
  'stock_report.warehouse': {
    title: 'Warehouse',
    short: 'When chosen, all figures use only that warehouse’s movements.',
  },
  'page.suppliers': { title: 'Suppliers', short: 'Companies you buy from, their balances and performance.' },
  'page.supplier': { title: 'Supplier', short: 'Supplier details, ledger statement and orders.' },
  'page.purchase_orders': {
    title: 'Purchasing',
    short: 'Purchase requests and orders placed with suppliers.',
  },
  'page.purchase_order': {
    title: 'Purchase order',
    short: 'Lines, received and remaining quantities, receipts.',
  },
  'page.purchase_order_new': {
    title: 'New purchase request',
    short: 'Purchase request with supplier, warehouse and products.',
  },
  'page.goods_receipt': {
    title: 'Goods receipt',
    short: 'Receive supplier deliveries into stock against orders.',
  },
  'purchasing.code': { title: 'Supplier code', short: 'Short unique code (letters, digits, dot, dash).' },
  'purchasing.payment_terms': {
    title: 'Payment terms',
    short: 'Invoice due date = receipt day + this many days. 0: cash.',
  },
  'purchasing.lead_time': {
    title: 'Lead time',
    short: 'Expected days from order to delivery; used for expected date and on-time rate.',
  },
  'purchasing.unit_cost': {
    title: 'Unit cost and VAT',
    short: 'Unit cost excl. VAT in the order currency; empty VAT uses the product tax class.',
  },
  'purchasing.place_order': {
    title: 'Place order',
    short: 'Marks the request as ordered; no message is sent to the supplier.',
  },
  'purchasing.receive': {
    title: 'Goods receipt',
    short: 'Quantity cannot exceed the remaining quantity unless over-delivery is accepted.',
  },
  'purchasing.update_cost': {
    title: 'Cost update',
    short: 'Moving weighted average of current stock and received goods.',
  },
  'purchasing.ledger_entry': {
    title: 'Ledger entry',
    short: 'Payments and returns reduce debt; entries are corrected by reversal.',
  },
  'purchasing.performance': {
    title: 'Supplier performance',
    short: 'On-time delivery, fill rate, short/over lines, lead time.',
  },
  'page.report_builder': {
    title: 'Report builder',
    short:
      'Build your own report from a dataset, metrics and dimensions; pivot table, chart, save and share.',
  },
  'report_builder.metrics': {
    title: 'Metrics and dimensions',
    short: 'Metrics are summed values; dimensions group them.',
  },
  'report_builder.pivot': {
    title: 'Pivot table',
    short: 'A column dimension turns values into columns (single metric, up to 36).',
  },
  'report_builder.share': {
    title: 'Sharing',
    short: 'Shared people see data with their own permissions; only the owner edits.',
  },
  'dashboard.widgets': {
    title: 'Report widgets',
    short: 'Saved reports on your dashboard as a table, chart or total; personal, up to 12.',
  },
  'reports.print': {
    title: 'Print / PDF',
    short:
      'Prints the report with the browser; choose “Save as PDF” to get a PDF. Menus and buttons are not printed.',
  },
  'page.report_files': {
    title: 'My report files',
    short: 'Large report files prepared in the background and scheduled reports; files are kept for 30 days.',
  },
  'report_files.background': {
    title: 'Prepare in background',
    short:
      'Turns the report into a file of up to 50,000 rows in the background; download it from “My report files”.',
  },
  'report_files.schedule': {
    title: 'Scheduled report',
    short:
      'Sent daily (previous day), weekly (previous Monday–Sunday) or monthly (previous month); never twice per period.',
  },
  'report_files.recipients': {
    title: 'Recipients',
    short:
      'Organization members only; each file uses the recipient’s permissions. The email contains only a link.',
  },
  'report_files.enabled': {
    title: 'Sending on',
    short: 'An off schedule does not run; turning it on recalculates the next send.',
  },
  'page.payment_disputes': {
    title: 'Payment disputes',
    short:
      'Chargebacks and information requests reported by the bank or provider: status, deadline, documents, finance impact.',
  },
  'page.payment_dispute': {
    title: 'Payment dispute',
    short: 'Dispute details, status, documents, notes and history.',
  },
  'disputes.status': {
    title: 'Dispute status',
    short:
      'Open → Evidence submitted → Won or Lost; Accepted means not contested. Lost/accepted amounts reduce profit and cash.',
  },
  'disputes.amount': {
    title: 'Dispute amount and fee',
    short:
      'The amount cannot exceed the undisputed part of the payment; the fee is charged regardless of outcome.',
  },
  'disputes.documents': {
    title: 'Add document',
    short: 'Attach evidence (PDF, JPEG, PNG; 10 MB) as a private file.',
  },
  'page.price_simulation': {
    title: 'Price simulation',
    short:
      'Shows profit after fees per product and for the period if price, cost or fees change; prices are not changed.',
  },
  'price_simulation.scenario': {
    title: 'Scenario',
    short: 'Change price and cost by a percentage (e.g. 10 increase, -5 discount).',
  },
  'price_simulation.fee': {
    title: 'Fee rate',
    short: 'Empty: the effective fee rate of payments captured in the period (with your fee rules).',
  },
  'price_simulation.volume': {
    title: 'Sales volume period',
    short:
      'Units sold in the list’s store in the last 30, 90 or 365 days including today (cancellations deducted).',
  },
  'page.marketing_summary': {
    title: 'Marketing and SEO summary',
    short:
      'Storefront funnel, new customers and CAC, 12-month customer value, ads, organic search, campaigns and order sources.',
  },
  'marketing_summary.funnel': {
    title: 'Storefront funnel',
    short:
      'Added to cart → order step (incl. pending payment) → confirmed order. Views are not stored on the server.',
  },
  'marketing_summary.cac': {
    title: 'Ad cost per new customer (CAC)',
    short: 'All ad spend in the period ÷ new customers in the period.',
  },
  'marketing_summary.value': {
    title: '12-month customer value',
    short: 'Net sales of the last 365 days ÷ customers who bought; historical, not a forecast.',
  },
  'marketing_summary.sources': {
    title: 'Last-touch source',
    short: 'UTM source of storefront orders; orders without cookie consent are shown separately.',
  },
  'page.multi_touch': {
    title: 'Multi-touch attribution',
    short:
      'Splits confirmed storefront orders across sources with first-touch, last-touch, linear and position-based (U) models.',
  },
  'multi_touch.models': {
    title: 'Attribution models',
    short:
      'First/last: all credit to that touch. Linear: equal. Position-based (U): 40% first and last, 20% to middle touches.',
  },
  'page.workflows': {
    title: 'Automation flows',
    short:
      'Trigger → Condition → Action: flows that check conditions and run actions on their own when something happens.',
    whatItDoes:
      'Lists your flows, their status and run counts for the last 7 days; creates drafts from 10 ready-made examples. A flow handles events after it is turned on; actions run with the current permissions of the person who turned it on; the same event is handled once per flow. Temporary errors are retried after 1 min, 5 min, 30 min and 2 hours; every step is logged. Customer messages stay in their own sections with consent and IYS checks.',
  },
  'page.workflow': {
    title: 'Automation flow',
    short: "The flow's trigger, conditions and actions; testing, turning on/pausing and the run log.",
    whatItDoes:
      'Set up the flow in three steps: 1) when (trigger), 2) under which conditions (field + comparison + value; all or any), 3) what to do (actions in order). "Try with recent events" evaluates the conditions with real recent events without saving; no action runs.',
  },
  'page.workflow_run': {
    title: 'Automation run',
    short: 'How an event was handled: event fields, condition results and every action attempt.',
    whatItDoes:
      'A failed run can be retried from the action where it stopped (completed actions do not run again); a pending run can be cancelled. The step log cannot be changed or deleted.',
  },
  'workflows.templates': {
    title: 'Ready-made examples',
    short: '10 example flows: 8 open here as drafts; 2 that message customers run in their own sections.',
  },
  'workflows.linked': {
    title: 'Customer message examples',
    short:
      'Cart reminders and back-in-stock notices go to customers and are managed in their own sections for consent, IYS and frequency caps.',
  },
  'workflows.trigger': {
    title: 'Trigger',
    short:
      'The event that starts the flow. Events are picked up every minute; earlier events are not handled and the same event is never handled twice.',
  },
  'workflows.conditions': {
    title: 'Conditions',
    short:
      'Comparisons with the event fields (at most 10). An event that does not match is logged as "skipped".',
  },
  'workflows.actions': {
    title: 'Actions',
    short:
      'Run in order (at most 8) with the permissions of the person who turned the flow on. A skipped action does not block the next one.',
  },
  'workflows.recipients': {
    title: 'Notice recipients',
    short:
      'Panel members only (at most 20). With "by permission" the permission at send time counts, and the store scope for store events.',
  },
  'workflows.limits': {
    title: 'Limits',
    short:
      'Wait: events within this time after a run are skipped. If the hourly limit is exceeded the flow pauses itself and managers are emailed.',
  },
  'workflows.test': {
    title: 'Try with recent events',
    short: "Evaluates the conditions with the trigger's last 5 events without saving. No action runs.",
  },
  'workflows.pause_all': {
    title: 'Pause all',
    short: 'Emergency stop: all active flows pause and pending runs are cancelled.',
  },
  'workflows.run_as': {
    title: 'Person who turned the flow on',
    short:
      "Actions run with this person's CURRENT permissions; the flow pauses itself if the membership is closed or a needed permission is lost.",
  },
  'workflows.runs': {
    title: 'Run log',
    short:
      'Every event is a run: pending, running, succeeded, failed, skipped or cancelled. The detail shows every action attempt.',
  },
  'workflows.retry': {
    title: 'Retry and cancel',
    short:
      'A failed run is retried from the action where it stopped (the flow must be on); a pending run can be cancelled.',
  },
};
